import React, { useEffect, useState } from 'react';
import Flag from 'react-flagkit';
import { getName } from 'country-list';
import { useRecoilState, useRecoilValue } from 'recoil';

import Typography from '@/ui-components/Typography';
import Button from '@/ui-components/Button';
import Gem from '@/ui-components/Gem';
import Loading from '@/ui-components/Loading';

import useBem from '@/use/useBem';

import { IUser, loadUser, completeTutorial, pendingPopupsState, userSelector } from '@/framework/user';
import { GemType, Gems, normalizeGemType } from '@/framework/gem';
import * as gemApi from '@/api/gem.service';
import type { OwnedGem } from '@/api/gem.service';
import SparksBadge from '@/ui-components/SparksBadge';

export type ProfileScreenProps = {
  user: IUser | null;
};

const resolveGemType = (name: string): GemType => {
  const lower = name.toLowerCase();
  if (lower === 'standard') {
    return 'red';
  }
  if (lower in Gems) {
    return lower as GemType;
  }
  return 'gold';
};

const ProfileScreen = (props: ProfileScreenProps) => {
  const { user } = props;
  const { bem } = useBem('profile-screen');
  const viewer = useRecoilValue(userSelector.user);
  const [pendingPopups, setPendingPopups] = useRecoilState(pendingPopupsState);
  const [gems, setGems] = useState<OwnedGem[]>([]);
  const [loadingGems, setLoadingGems] = useState(true);
  const [equippingId, setEquippingId] = useState<number | null>(null);
  const [tutorialLoading, setTutorialLoading] = useState(false);

  const isOwnProfile =
    viewer?.id != null && user?.id != null && viewer.id === user.id;
  const tutorial = isOwnProfile
    ? pendingPopups.find((popup) => popup.type === 'tutorial')
    : undefined;

  useEffect(() => {
    if (!isOwnProfile) {
      setLoadingGems(false);
      return;
    }

    let cancelled = false;

    async function fetchGems() {
      try {
        const data = await gemApi.getGems();
        if (!cancelled) setGems(data ?? []);
      } catch {
        if (!cancelled) setGems([]);
      } finally {
        if (!cancelled) setLoadingGems(false);
      }
    }

    fetchGems();
    return () => {
      cancelled = true;
    };
  }, [isOwnProfile]);

  if (!user) {
    return null;
  }

  const handleEquip = async (gemId: number) => {
    setEquippingId(gemId);
    try {
      await gemApi.setActiveGem(gemId);
      await loadUser();
    } catch {
      // keep current selection
    } finally {
      setEquippingId(null);
    }
  };

  const handleTutorialDismiss = async () => {
    setTutorialLoading(true);
    try {
      await completeTutorial();
      setPendingPopups((prev) => prev.filter((popup) => popup.type !== 'tutorial'));
    } catch {
      // keep tutorial visible on failure
    } finally {
      setTutorialLoading(false);
    }
  };

  return (
    <div className={bem('')}>
      {tutorial && (
        <div className={bem('tutorial')}>
          <Typography variant="m" className={bem('tutorial-title')}>
            Your profile is unlocked
          </Typography>
          <Typography variant="s" className={bem('tutorial-text')}>
            Edit your details and pick a gem here. Store and Rewards are in the
            nav.
          </Typography>
          <Button
            size="medium"
            onClick={handleTutorialDismiss}
            loading={tutorialLoading}
          >
            Got it
          </Button>
        </div>
      )}

      <div className={bem('top')}>
        {!isOwnProfile && user.activeGem && (
          <div className={bem('equipped')}>
            <Gem
              type={normalizeGemType(user.activeGem)}
              size="small"
              state="selected"
            />
          </div>
        )}
        <table className={bem('table')}>
          <tbody>
            <tr>
              <td>
                <Typography variant="m">Username</Typography>
              </td>
              <td>
                <Typography variant="m">
                  {user.country && (
                    <span className={bem('flag')}>
                      <Flag country={user.country.toUpperCase()} size={18} />
                    </span>
                  )}
                  {user.username}
                </Typography>
              </td>
            </tr>

            {user.country && (
              <tr>
                <td>
                  <Typography variant="m">Country</Typography>
                </td>
                <td>
                  <Typography variant="m">
                    {getName(user.country.toUpperCase())}
                  </Typography>
                </td>
              </tr>
            )}
            <tr>
              <td>
                <Typography variant="m">Level</Typography>
              </td>
              <td>
                <Typography variant="m">{user.level ?? 1}</Typography>
              </td>
            </tr>
            {isOwnProfile && (
              <>
                <tr>
                  <td>
                    <Typography variant="m">XP</Typography>
                  </td>
                  <td>
                    <Typography variant="m">
                      {user.xpIntoLevel ?? 0} / {user.xpForNext ?? 100}
                    </Typography>
                  </td>
                </tr>
                <tr>
                  <td>
                    <Typography variant="m">Sparks</Typography>
                  </td>
                  <td>
                    <SparksBadge compact amount={user.sparks ?? 0} />
                  </td>
                </tr>
              </>
            )}
            {user.rank != null && (
              <tr>
                <td>
                  <Typography variant="m">Rank</Typography>
                </td>
                <td>
                  <Typography variant="m">{user.rank}</Typography>
                </td>
              </tr>
            )}
            <tr>
              <td>
                <Typography variant="m">Score</Typography>
              </td>
              <td>
                <Typography variant="m">{user.score}</Typography>
              </td>
            </tr>
            <tr>
              <td>
                <Typography variant="m">Won / Lost</Typography>
              </td>
              <td>
                <Typography variant="m">
                  {user.wins} / {user.losses}
                </Typography>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {isOwnProfile && (
        <div className={bem('gems')}>
          <Typography variant="m" className={bem('gems-title')}>
            Gems
          </Typography>
          {loadingGems ? (
            <Loading />
          ) : gems.length === 0 ? (
            <Typography variant="s">No gems yet.</Typography>
          ) : (
            <div className={bem('gems-grid')}>
              {gems.map((gem) => {
                const gemType = resolveGemType(gem.name);
                const isActive = user.activeGem === gemType;
                const isEquipping = equippingId === gem.id;

                return (
                  <button
                    key={gem.id}
                    type="button"
                    className={bem('gem-btn', { active: isActive })}
                    disabled={isEquipping}
                    onClick={() => handleEquip(gem.id)}
                  >
                    <Gem type={gemType} size="small" state={isActive ? 'selected' : 'normal'} />
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProfileScreen;
